import React, { useEffect, useState } from "react";
import college from "../config/collegeConfig";
import { apiJson, api, apiBlob, API_BASE } from "../utils/api";

const NATIONALITY = ["INDIAN", "OTHER"];
const RELIGION = ["HINDU", "MUSLIM", "CHRISTIAN", "SIKH", "BUDDHIST", "JAIN", "OTHER"];
const GENDER = ["MALE", "FEMALE", "OTHER"];
const CASTE = ["UR", "OBC", "SC", "ST", "BC", "EBC", "EWS", "PH", "URF"];

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "-";

const SELECT_OPTIONS = {
  gender: GENDER,
  casteCategory: CASTE,
  nationality: NATIONALITY,
  religion: RELIGION,
};

// Editable fields shown on Profile (subset of admission form's personal info).
const EDITABLE = [
  { key: "name",          label: "Name" },
  { key: "fatherName",    label: "Father's Name" },
  { key: "motherName",    label: "Mother's Name" },
  { key: "gender",        label: "Gender" },
  { key: "casteCategory", label: "Caste Category" },
  { key: "religion",      label: "Religion" },
  { key: "nationality",   label: "Nationality" },
  { key: "motherTongue",  label: "Mother Tongue" },
  { key: "aadhaar",       label: "Aadhaar" },
];

// Locked once admission is confirmed (mirrors backend studentLock.js)
const LOCKED_FIELDS = ["name", "fatherName", "motherName", "gender", "aadhaar"];

const Profile = () => {
  const [student, setStudent] = useState(null);
  const [admission, setAdmission] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const load = async () => {
    try {
      const data = await apiJson("/api/student/admission");
      setStudent(data.student);
      setAdmission(data.admission);
      setDraft(
        EDITABLE.reduce((acc, f) => ({ ...acc, [f.key]: data.student[f.key] || "" }), {})
      );
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); }, []);

  // Fetch preview PNG (authenticated) whenever admission/photo changes and not yet admitted
  useEffect(() => {
    if (!student || !admission) return;
    if (admission.status === "ADMITTED") return;
    let cancelled = false;
    apiBlob("/api/student/id-card?preview=1")
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setPreviewUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [student, admission]);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      // Use the same form endpoint as the admission form, but as draft so it doesn't bump status
      await apiJson("/api/student/admission/form", {
        method: "PUT",
        body: JSON.stringify({ isDraft: true, personalInfo: draft }),
      });
      setMessage("Profile updated");
      setEditing(false);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const res = await api("/api/student/photo", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Upload failed");
      setMessage("Photo updated");
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleIdCard = async () => {
    try {
      const blob = await apiBlob("/api/student/id-card");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `id_card_${student.applyId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("ID card not available — admission must be confirmed first");
    }
  };

  if (error && !student) return <p className="text-red-600">{error}</p>;
  if (!student) return <p className="text-gray-500">Loading...</p>;

  const isAdmitted = admission?.status === "ADMITTED";
  const isFieldLocked = (key) => isAdmitted && LOCKED_FIELDS.includes(key);

  const renderField = (key, label, value) => {
    const isMissing = !value;
    const locked = isFieldLocked(key);
    if (!editing || locked) {
      return (
        <div className={`p-3 rounded border ${locked ? "bg-gray-100 border-gray-300" : isMissing ? "bg-yellow-50 border-yellow-300" : "bg-gray-50 border-gray-200"}`}>
          <p className="text-xs text-gray-500">
            {label} {locked && <span title="Locked after admission">🔒</span>}
          </p>
          <p className={`text-sm font-medium ${isMissing && !locked ? "text-yellow-700 italic" : "text-gray-800"}`}>
            {value || (locked ? "—" : "Not provided")}
          </p>
        </div>
      );
    }
    const options = SELECT_OPTIONS[key];
    return (
      <div className="p-3 rounded border bg-white border-indigo-200">
        <label className="text-xs text-gray-500 block mb-1">{label}</label>
        {options ? (
          <select
            value={draft[key] || ""}
            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            className="w-full px-2 py-1 border rounded text-sm"
          >
            <option value="">-- Select --</option>
            {options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={draft[key] || ""}
            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            className="w-full px-2 py-1 border rounded text-sm"
          />
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="text-center mb-4">
        {college.logo && (
          <img src={college.logo} alt="College Logo" className="mx-auto w-20 h-20 object-contain mb-2" />
        )}
        <h2 className="text-xl font-bold text-gray-800">{college.name}</h2>
        <p className="text-sm text-gray-500">{college.tagline}</p>
      </div>

      {message && <div className="bg-green-50 text-green-700 p-2 rounded mb-3 text-sm">{message}</div>}
      {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-3 text-sm">{error}</div>}

      <div className="flex flex-col md:flex-row gap-6">
        {/* Photo + ID card */}
        <div className="md:w-48 flex-shrink-0 text-center">
          {student.photoUrl ? (
            <img
              src={`${API_BASE}${student.photoUrl}`}
              alt="student"
              className="w-40 h-48 object-cover mx-auto border rounded mb-2"
            />
          ) : (
            <div className="w-40 h-48 bg-gray-100 border mx-auto flex items-center justify-center text-xs text-gray-500 mb-2">
              No photo
            </div>
          )}
          {isAdmitted ? (
            <p className="text-xs text-gray-500 mb-3">🔒 Photo locked after admission</p>
          ) : (
            <>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhoto}
                disabled={photoUploading}
                className="text-xs w-full mb-3"
              />
              {photoUploading && <p className="text-xs text-gray-500">Uploading...</p>}
            </>
          )}

          {isAdmitted ? (
            <button
              onClick={handleIdCard}
              className="w-full bg-emerald-600 text-white text-sm py-2 rounded hover:bg-emerald-700"
            >
              Download ID Card
            </button>
          ) : (
            <IdCardPreview student={student} admission={admission} previewUrl={previewUrl} />
          )}
        </div>

        {/* Fields */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-700">Personal Details</h3>
            {editing ? (
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-indigo-600 text-white text-sm px-4 py-1.5 rounded hover:bg-indigo-700"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => { setEditing(false); load(); }}
                  className="bg-gray-200 text-gray-700 text-sm px-4 py-1.5 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="bg-indigo-600 text-white text-sm px-4 py-1.5 rounded hover:bg-indigo-700"
              >
                Edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {EDITABLE.map((f) => (
              <div key={f.key}>{renderField(f.key, f.label, student[f.key])}</div>
            ))}

            {/* Read-only fields */}
            <div className="p-3 rounded border bg-gray-50 border-gray-200">
              <p className="text-xs text-gray-500">Apply ID</p>
              <p className="text-sm font-medium text-gray-800">{student.applyId}</p>
            </div>
            <div className="p-3 rounded border bg-gray-50 border-gray-200">
              <p className="text-xs text-gray-500">Date of Birth</p>
              <p className="text-sm font-medium text-gray-800">{formatDate(student.dateOfBirth)}</p>
            </div>
            <div className="p-3 rounded border bg-gray-50 border-gray-200">
              <p className="text-xs text-gray-500">Roll Number</p>
              <p className="text-sm font-medium text-gray-800">{admission?.rollNumber || "—"}</p>
            </div>
            <div className="p-3 rounded border bg-gray-50 border-gray-200">
              <p className="text-xs text-gray-500">Course</p>
              <p className="text-sm font-medium text-gray-800">{admission?.degree?.name || "—"}</p>
            </div>
            <div className="p-3 rounded border bg-gray-50 border-gray-200">
              <p className="text-xs text-gray-500">Current Semester</p>
              <p className="text-sm font-medium text-gray-800">{admission?.currentSemester || "—"}</p>
            </div>
            <div className="p-3 rounded border bg-gray-50 border-gray-200">
              <p className="text-xs text-gray-500">Enrollment Session</p>
              <p className="text-sm font-medium text-gray-800">{admission?.academicSession?.name || "—"}</p>
            </div>
            <div className="p-3 rounded border bg-gray-50 border-gray-200">
              <p className="text-xs text-gray-500">Status</p>
              <p className="text-sm font-medium text-gray-800">{admission?.status || "—"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ID Card preview + checklist (shown until admission is confirmed) ──
const IdCardPreview = ({ student, admission, previewUrl }) => {
  const status = admission?.status;
  const steps = [
    { label: "Apply ID confirmed", done: true },
    { label: "Photo uploaded", done: !!student.photoUrl },
    { label: "Form submitted", done: ["FORM_SUBMITTED", "PAYMENT_DONE", "VERIFIED", "ADMITTED"].includes(status) },
    { label: "Payment complete", done: ["PAYMENT_DONE", "VERIFIED", "ADMITTED"].includes(status) },
    { label: "Admin approval", done: status === "ADMITTED" },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="mt-1">
      <div className="relative w-full mb-3">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="ID Card preview"
            className="w-full border rounded"
            style={{ filter: "blur(3px)", opacity: 0.85 }}
          />
        ) : (
          <div className="w-full h-24 bg-gray-100 border rounded flex items-center justify-center text-xs text-gray-400">
            Loading preview...
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="bg-black/60 text-white text-xs px-2 py-0.5 rounded">PREVIEW</span>
        </div>
      </div>

      <div className="text-xs font-semibold text-gray-700 mb-1">
        ID Card progress — {pct}%
      </div>
      <div className="w-full bg-gray-200 rounded h-1.5 mb-2">
        <div className="bg-emerald-500 h-1.5 rounded" style={{ width: `${pct}%` }} />
      </div>
      <ul className="text-xs space-y-1 text-left">
        {steps.map((s, i) => (
          <li key={i} className={s.done ? "text-emerald-700" : "text-gray-500"}>
            {s.done ? "✓" : "○"} {s.label}
          </li>
        ))}
      </ul>
      <p className="text-xs text-gray-400 mt-2 italic">
        Complete the steps above to unlock your ID card.
      </p>
    </div>
  );
};

export default Profile;
